import React from "react"
import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs))
}

/**
 * Chuyen chuoi tieng Viet co dau thanh khong dau + lowercase.
 * Dung cho search/filter: "Việt Nam" → "viet nam", "đường" → "duong"
 */
export function normalizeVi(str: string): string {
    return str
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'd');
}

/**
 * Kiem tra str co chua query hay khong (khong phan biet dau tieng Viet).
 * "Việt Nam".viIncludes("viet") → true
 */
export function viIncludes(str: string | null | undefined, query: string): boolean {
    if (!str) return false;
    return normalizeVi(str).includes(normalizeVi(query));
}

/**
 * Tìm kiếm gần đúng (fuzzy search) đa từ khóa, không phân biệt dấu.
 * Tất cả các từ khóa trong query phải xuất hiện trong chuỗi đích (không cần theo thứ tự).
 */
export function fuzzyMatchVi(str: string | null | undefined, query: string): boolean {
    if (!str) return false;
    if (!query.trim()) return true;

    const normalizedStr = normalizeVi(str);
    const queryWords = normalizeVi(query).split(/\s+/).filter(Boolean);

    return queryWords.every(word => normalizedStr.includes(word));
}

/**
 * Format date string/Date thành dạng ngày Việt Nam
 * @param value - ISO string hoặc Date object
 * @param format - 'short' (dd/MM), 'medium' (dd/MM/yyyy), 'long' (dd/MM/yyyy HH:mm), 'date-input' (yyyy-MM-dd)
 * @returns Chuỗi ngày đã format, hoặc chuỗi rỗng nếu không hợp lệ
 */
export function formatDate(
    value: string | Date | null | undefined,
    format: 'short' | 'medium' | 'long' | 'date-input' = 'medium'
): string {
    if (!value) return '';
    const d = typeof value === 'string' ? new Date(value) : value;
    if (Number.isNaN(d.getTime())) return typeof value === 'string' ? value : '';

    switch (format) {
        case 'short':
            return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' });
        case 'medium':
            return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
        case 'long':
            return d.toLocaleString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        case 'date-input': {
            // Format yyyy-MM-dd cho HTML input[type=date]
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        }
    }
}

export function formatDateTime(value: string | Date | null | undefined): string {
    return formatDate(value, 'long');
}

export function formatCurrency(amount: number | string | undefined | null): string {
    if (amount === undefined || amount === null) return '';
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (Number.isNaN(num)) return '';
    return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(num);
}

/**
 * Format number as integer with thousand separators (vi-VN locale)
 * Use for: quantities, VND amounts, stock counts
 * 1000000 → "1.000.000"
 * 1234.56 → "1.235" (rounded to integer)
 */
export function formatNumber(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '') return '—';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (Number.isNaN(num)) return '—';
    return new Intl.NumberFormat('vi-VN', {
        maximumFractionDigits: 0,
    }).format(num);
}

/**
 * Format number with decimal places + thousand separators
 * Use for: foreign currency amounts (USD, EUR, etc.)
 * (1234.567, 3) → "1.234,567"
 * (1234.5, 2)   → "1.234,50"
 */
export function formatDecimal(
    value: number | string | null | undefined,
    decimals: number = 3
): string {
    if (value === null || value === undefined || value === '') return '—';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (Number.isNaN(num)) return '—';
    return new Intl.NumberFormat('vi-VN', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(num);
}

/**
 * Format monetary value — NO currency symbol appended.
 * Symbol should be shown in the column header/label instead.
 *
 * - VND (default): integer with thousand separators
 *   1000000 → "1.000.000"
 * - USD/foreign: 3 decimal places with thousand separators
 *   1234.567 → "1.234,567"
 */
export function formatMoney(
    value: number | string | null | undefined,
    currency?: string
): string {
    if (value === null || value === undefined || value === '') return '—';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (Number.isNaN(num)) return '—';

    // Foreign currency → 3 decimal places
    if (currency && currency !== 'VND') {
        return formatDecimal(num, 3);
    }

    // VND or default → integer with thousand separators
    return formatNumber(num);
}

/**
 * Resolves a relative upload URL (e.g. /uploads/image.png) to a full absolute URL
 * pointing to the backend API server.
 */
export function getUploadUrl(url: string | null | undefined): string {
    if (!url) return '';
    const backendUrl = (import.meta.env.VITE_API_URL || "http://localhost:3006/api").replace(/\/api$/, '');

    // Fix absolute DB records that were saved with localhost during dev
    if (url.startsWith('http://localhost') || url.startsWith('http://127.0.0.1')) {
        try {
            const parsedUrl = new URL(url);
            return backendUrl + parsedUrl.pathname;
        } catch {
            /* ignore */
        }
    }

    if (url.startsWith('http')) return url;
    return backendUrl + (url.startsWith('/') ? url : '/' + url);
}

/**
 * Giới hạn dung lượng tối đa cho tệp đính kèm kỹ thuật & bản vẽ: 100 MB
 */
export const MAX_ATTACHMENT_SIZE = 100 * 1024 * 1024;

/**
 * Giới hạn dung lượng tối đa cho tệp Import Excel (.xlsx, .xls, .csv): 25 MB
 */
export const MAX_EXCEL_IMPORT_SIZE = 25 * 1024 * 1024;

/**
 * Giới hạn dung lượng tối đa cho ảnh chụp sàn xưởng, maquette, kiểm tra lỗi: 10 MB
 */
export const MAX_MEDIA_IMAGE_SIZE = 10 * 1024 * 1024;

/**
 * Giới hạn dung lượng tối đa cho Avatar, Logo công ty, Con dấu, Chữ ký số: 5 MB
 */
export const MAX_AVATAR_LOGO_SIZE = 5 * 1024 * 1024;

export interface UploadValidationResult {
    valid: boolean;
    error?: string;
}

export interface UploadValidationOptions {
    maxSize?: number;
    allowedExtensions?: string[];
    label?: string;
}

/**
 * Helper kiểm tra an toàn dung lượng và định dạng tệp trước khi tải lên (Client Gating 0ms).
 * Hỗ trợ đồng thời 2 chữ ký gọi hàm trong toàn hệ thống:
 * 1) validateUploadFile(file, maxSizeNumber, labelString) -> Trả về { valid: boolean, error?: string } (không throw)
 * 2) validateUploadFile(file, optionsObject?) -> Ném Error tiếng Việt nếu không hợp lệ (tương thích try/catch) và trả về { valid: true }
 */
export function validateUploadFile(
    file: File,
    maxSize: number,
    label?: string
): UploadValidationResult;
export function validateUploadFile(
    file: File,
    options?: UploadValidationOptions
): UploadValidationResult;
export function validateUploadFile(
    file: File,
    maxSizeOrOptions?: number | UploadValidationOptions,
    labelArg?: string
): UploadValidationResult {
    const isTupleCall = typeof maxSizeOrOptions === 'number';
    const options: UploadValidationOptions = isTupleCall
        ? { maxSize: maxSizeOrOptions, label: labelArg }
        : (maxSizeOrOptions ?? {});

    const maxSize = options.maxSize ?? MAX_ATTACHMENT_SIZE;
    const label = options.label ?? 'Tệp';

    if (file.size > maxSize) {
        const errorMsg = `${label} "${file.name}" (${formatBytes(file.size)}) vượt quá dung lượng tối đa cho phép (${formatBytes(maxSize)}). Vui lòng nén file hoặc chia sẻ liên kết Google Drive.`;
        if (isTupleCall) {
            return { valid: false, error: errorMsg };
        }
        throw new Error(errorMsg);
    }

    if (options.allowedExtensions && options.allowedExtensions.length > 0) {
        const lastDotIndex = file.name.lastIndexOf('.');
        const ext = lastDotIndex >= 0 ? file.name.substring(lastDotIndex).toLowerCase() : '';
        const isAllowed = options.allowedExtensions.some(
            (allowed) => allowed.toLowerCase() === ext
        );
        if (!isAllowed) {
            const errorMsg = `Định dạng tệp "${ext || 'không xác định'}" không được hỗ trợ cho ${label.toLowerCase()}. Vui lòng chọn tệp có định dạng: ${options.allowedExtensions.join(', ')}.`;
            if (isTupleCall) {
                return { valid: false, error: errorMsg };
            }
            throw new Error(errorMsg);
        }
    }

    return { valid: true };
}

/**
 * Helper kiểm tra an toàn tệp Excel trước khi parse hoặc nạp vào bộ nhớ.
 */
/**
 * Kiểm tra file Excel trước khi nhập: trả về { valid, error } (không ném lỗi).
 * Các màn nhập (đơn hàng, báo giá, kho, kẽm CTP, hộp nhập chung) đọc kết quả này; trước đây hàm trả void
 * nên mọi lần chọn file đều lỗi "Cannot read properties of undefined".
 */
export function validateExcelFile(file: File, maxSize = MAX_EXCEL_IMPORT_SIZE): UploadValidationResult {
    try {
        validateUploadFile(file, {
            maxSize,
            allowedExtensions: ['.xlsx', '.xls', '.csv'],
            label: 'Tệp bảng tính Excel',
        });
        return { valid: true };
    } catch (e: any) {
        return { valid: false, error: e?.message || 'Tệp bảng tính Excel không hợp lệ' };
    }
}

/**
 * Định dạng số bytes thành chuỗi hiển thị thân thiện (B, KB, MB, GB)
 */
export function formatBytes(bytes: number, decimals = 1): string {
    if (!bytes || bytes <= 0) return '0 B';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const safeIndex = Math.min(i, sizes.length - 1);
    return `${parseFloat((bytes / Math.pow(k, safeIndex)).toFixed(dm))} ${sizes[safeIndex]}`;
}

/**
 * Trích xuất chuỗi thông báo lỗi an toàn từ bất kỳ đối tượng lỗi nào.
 * Re-export từ @apv-erp/status để backward compatible.
 */
export { classifyError, extractErrorMessage, extractZodFieldErrors, getErrorTitle, hasZodErrors } from '@apv-erp/status';


/**
 * Rút gọn tên vị trí lưu trữ (ví dụ: "SLOT_A" -> "S.A", "SLOT-1" -> "S.1")
 * @param loc - Tên vị trí đầy đủ
 * @returns Tên vị trí đã rút gọn
 */
export function formatLocation(loc: string | null | undefined): string {
    if (!loc) return '';
    return loc.replace(/SLOT[-_]/gi, 'S.');
}

/**
 * Chuẩn hóa an toàn bất kỳ giá trị ngày nào (Date, string ISO, timestamp, null, undefined)
 * về định dạng chuỗi `yyyy-MM-dd` tương thích 100% với HTML5 `<input type="date">`.
 * Sử dụng Local Time của client để chống lỗi nhảy lùi ngày do chênh lệch UTC/Local timezone.
 */
export function safeFormatDateInput(val: unknown): string {
    if (!val) return '';
    if (val instanceof Date) {
        if (Number.isNaN(val.getTime())) return '';
        const y = val.getFullYear();
        const m = String(val.getMonth() + 1).padStart(2, '0');
        const d = String(val.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }
    if (typeof val === 'string') {
        const trimmed = val.trim();
        if (!trimmed) return '';
        // Chuỗi yyyy-MM-dd chuẩn 10 ký tự
        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
            return trimmed;
        }
        // Chuỗi ISO có 'T' (ví dụ 2026-09-30T00:00:00.000Z)
        if (trimmed.includes('T')) {
            const parsed = new Date(trimmed);
            if (!Number.isNaN(parsed.getTime())) {
                const y = parsed.getFullYear();
                const m = String(parsed.getMonth() + 1).padStart(2, '0');
                const d = String(parsed.getDate()).padStart(2, '0');
                return `${y}-${m}-${d}`;
            }
            return trimmed.split('T')[0];
        }
        // Chuỗi bắt đầu bằng yyyy-MM-dd
        if (trimmed.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
            return trimmed.substring(0, 10);
        }
        const parsed = new Date(trimmed);
        if (!Number.isNaN(parsed.getTime())) {
            const y = parsed.getFullYear();
            const m = String(parsed.getMonth() + 1).padStart(2, '0');
            const d = String(parsed.getDate()).padStart(2, '0');
            return `${y}-${m}-${d}`;
        }
        return '';
    }
    if (typeof val === 'number') {
        const parsed = new Date(val);
        if (!Number.isNaN(parsed.getTime())) {
            const y = parsed.getFullYear();
            const m = String(parsed.getMonth() + 1).padStart(2, '0');
            const d = String(parsed.getDate()).padStart(2, '0');
            return `${y}-${m}-${d}`;
        }
    }
    return '';
}

/**
 * Làm sạch tiền tố hệ thống nhãn hiệu (brands_, brand_) khỏi tên vật tư / sản phẩm
 */
export function cleanBrandName(name?: string | null): string {
    if (!name) return "";
    return String(name)
        .replace(/brands?_/gi, "")
        .replace(/\s+/g, " ")
        .trim();
}

/**
 * Làm sạch chuỗi công đoạn sản xuất, loại bỏ trùng lặp với quy cách và format chuẩn
 */
export function cleanProcessText(processText?: string | null, specsText?: string | null): string {
    if (!processText) return "";
    let cleaned = String(processText).replace(/\+/g, ", ");

    // 1. Remove duplicate specs from process
    if (specsText) {
        const escapedSpecs = specsText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const specRegex = new RegExp(`(?:,\\s*)?${escapedSpecs}(?:\\s*,)?`, "gi");
        cleaned = cleaned
            .replace(specRegex, ", ")
            .replace(/,\s*$/, "")
            .replace(/^,\s*/, "")
            .trim();
    }

    // 2. Format material names if they match pattern like "I350 Ivory" -> "Ivory 350gsm"
    cleaned = cleaned.replace(
        /(^|,\s*)([A-Z])(\d{2,3})\s+([a-zA-Z0-9_-]+)/gi,
        (match, prefix, letter, weight, name) => {
            return `${prefix}${name} ${weight}gsm`;
        },
    );

    return cleaned.trim();
}

/**
 * Làm sạch và xác thực an toàn đường dẫn quay lại (Anti-Open Redirect)
 * Chỉ chấp nhận internal path bắt đầu bằng '/', cấm '//', 'http:', 'https:', 'javascript:'.
 */
export function sanitizeReturnUrl(url?: string | null, fallback: string = '/products'): string {
    if (!url || typeof url !== 'string') return fallback;
    const trimmed = url.trim();
    if (trimmed.startsWith('/') && !trimmed.startsWith('//') && !trimmed.includes('javascript:') && !trimmed.includes('data:')) {
        return trimmed;
    }
    return fallback;
}

/**
 * Chuẩn hóa giá trị hiển thị trên các trường thông tin chi tiết (<F />).
 * - Giữ nguyên ReactElement hợp lệ (<EntityLink />, <Badge />, ...)
 * - Trích xuất tên hiển thị an toàn nếu truyền object quan hệ Prisma (triệt tiêu "[object Object]")
 * - Chuẩn hóa rỗng / null / undefined thành "—"
 */
export function renderDetailValue(value: unknown): React.ReactNode {
    if (value === undefined || value === null || value === '') return '—';
    if (React.isValidElement(value)) return value;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    if (Array.isArray(value)) {
        const parts = value
            .map((item) => renderDetailValue(item))
            .filter((item) => item !== '—');
        if (parts.length === 0) return '—';
        if (parts.every((p) => typeof p === 'string')) return (parts as string[]).join(', ');
        return parts;
    }
    if (typeof value === 'object') {
        const record = value as Record<string, unknown>;
        const candidate =
            record.fullName ??
            record.name ??
            record.productName ??
            record.shortName ??
            record.label ??
            record.title ??
            record.code;
        if (typeof candidate === 'string' && candidate.trim() !== '') {
            return candidate.trim();
        }
        return '—';
    }
    return String(value);
}
